<template>
  <div>
    <v-dialog
      v-model="internalValue"
      max-width="1100px"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-stethoscope</v-icon>
          <span class="ds-dialog-title">Médicos do Paciente</span>
          <v-spacer />
          <v-btn
            icon
            class="close-btn"
            color="white"
            aria-label="Ver tour desta janela"
            @click="startTour"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon class="close-btn" color="white" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- STEPPER -->
          <v-stepper v-model="currentStep" elevation="0" class="stepper-custom transparent">
            <v-stepper-header class="steps-header">
              <v-stepper-step
                :complete="currentStep > 1"
                step="1"
                color="primary"
                data-tour="pd-lista"
              >
                Lista de Médicos
                <small>Visualizar relações</small>
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step
                :complete="currentStep > 2"
                step="2"
                color="success"
                data-tour="pd-add"
              >
                Adicionar Relação
                <small>Vincular novo médico</small>
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <!-- STEP 1: LISTA DE MÉDICOS -->
              <v-stepper-content step="1" class="px-0 pb-0">
                <div>
                  <!-- Botão de adicionar em destaque -->
                  <div class="text-center mb-6" v-if="userType === 'assistant'">
                    <v-btn
                      large
                      color="success"
                      class="elevation-4 add-doctor-btn"
                      @click="goToStep2"
                    >
                      <v-icon left size="24">mdi-plus-circle</v-icon>
                      Adicionar Novo Médico
                    </v-btn>
                  </div>

                  <!-- Lista de médicos relacionados -->
                  <section class="ds-form-section">
                    <div class="ds-form-section__head">
                      <div class="ds-form-section__title">
                        <v-icon small color="primary" class="mr-2">mdi-account-group</v-icon>
                        <span>Médicos Vinculados</span>
                      </div>
                      <span v-if="!loadingRelatedDoctors" class="ds-form-section__aside">{{ doctorsRelated.length }}</span>
                    </div>

                    <v-container fluid class="pa-0">
                      <!-- LOADING -->
                      <v-row
                        v-if="loadingRelatedDoctors"
                        justify="center"
                        class="my-10"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="50"
                        />
                      </v-row>

                      <!-- LISTA -->
                      <v-row v-else-if="doctorsRelated.length" dense>
                        <v-col
                          v-for="doctor in doctorsRelated"
                          :key="doctor.id"
                          cols="12"
                          sm="6"
                          md="4"
                        >
                          <v-card outlined class="doctor-card" hover>
                            <v-card-title class="pa-3">
                              <v-avatar size="50" class="mr-3">
                                <v-img :src="doctor.imageUrl" />
                              </v-avatar>
                              <div class="flex-grow-1">
                                <div class="font-weight-medium text-subtitle-1">
                                  {{ doctor.name }}
                                </div>
                                <div class="text-caption grey--text">
                                  {{ doctor.email }}
                                </div>
                              </div>
                            </v-card-title>

                            <v-card-actions class="pa-3 pt-0">
                              <v-btn
                                color="error"
                                text
                                small
                                @click="confirmDeleteDoctor(doctor.id)"
                              >
                                <v-icon left small>mdi-delete</v-icon>
                                Remover
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-col>
                      </v-row>

                      <!-- EMPTY STATE (SÓ depois do fetch) -->
                      <div
                        v-else-if="hasFetchedDoctors"
                        class="text-center py-6"
                      >
                        <v-icon size="64" color="grey lighten-1" class="mb-4">
                          mdi-doctor
                        </v-icon>
                        <div class="text-h6 grey--text mb-2">
                          Nenhum médico vinculado
                        </div>
                        <div class="text-body-2 grey--text mb-4">
                          Adicione médicos para começar a gerenciar as relações
                        </div>

                        <v-btn
                          color="primary"
                          @click="goToStep2"
                          v-if="userType === 'assistant'"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Adicionar Primeiro Médico
                        </v-btn>
                      </div>
                    </v-container>
                  </section>
                </div>
              </v-stepper-content>

              <!-- STEP 2: FORMULÁRIO DE ADIÇÃO -->
              <v-stepper-content step="2" v-if="userType === 'assistant'" class="px-0 pb-0">
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-link-variant</v-icon>
                      <span>Nova relação</span>
                    </div>
                  </div>

                  <v-alert type="info" text dense class="mb-4">
                    <div class="font-weight-medium">Vincular Novo Médico</div>
                    <div class="text-caption">
                      Selecione a clínica e o médico para criar uma nova relação
                    </div>
                  </v-alert>

                  <v-form ref="relationForm" v-model="formValid">
                    <!-- Só mostra selects se for ASSISTENTE -->
                    <template v-if="userType === 'assistant'">
                      <!-- Select da clínica -->
                      <v-row>
                        <v-col cols="12">
                          <v-select
                            v-model="selectedClinic"
                            :items="clinics"
                            item-text="name"
                            item-value="id"
                            label="Selecione a clínica"
                            prepend-inner-icon="mdi-hospital-building"
                            outlined
                            color="primary"
                            clearable
                            :rules="[(v) => !!v || 'Selecione uma clínica']"
                            @change="onClinicChange"
                            class="clinic-select"
                          >
                            <template v-slot:item="{ item }">
                              <div class="py-2">
                                <div class="font-weight-medium">
                                  {{ item.name }}
                                </div>
                                <div class="text--secondary text-caption">
                                  CNPJ: {{ item.cnpj }} - {{ item.city }}/{{
                                    item.state
                                  }}
                                </div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>

                      <!-- Select do médico -->
                      <v-row>
                        <v-col cols="12">
                          <v-select
                            label="Selecione o médico"
                            v-model="selectedDoctor"
                            :items="doctors"
                            item-text="name"
                            item-value="id"
                            prepend-inner-icon="mdi-stethoscope"
                            outlined
                            color="primary"
                            clearable
                            :disabled="!selectedClinic"
                            :loading="loadingDoctors"
                            :rules="[(v) => !!v || 'Selecione um médico']"
                            class="doctor-select"
                          >
                            <template v-slot:item="{ item }">
                              <div class="py-2">
                                <div class="font-weight-medium">
                                  {{ item.name }}
                                </div>
                                <div class="text--secondary text-caption">
                                  CRM: {{ item.crm }} - {{ item.email }}
                                </div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Ações do formulário -->
                    <v-row class="mt-4">
                      <v-col cols="12">
                        <div class="d-flex justify-space-between">
                          <v-btn outlined @click="goToStep1" color="primary">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Voltar
                          </v-btn>

                          <v-btn
                            color="success"
                            :disabled="!selectedDoctor || !formValid"
                            :loading="creatingRelation"
                            @click="createRelation"
                            class="px-6"
                            depressed
                          >
                            <v-icon left>mdi-link</v-icon>
                            Vincular Médico
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </v-form>
                </section>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { fetchMe } from "@/services/authServices";
import { getMyClinics, getDoctorsByClinics } from "@/services/clinicService";
import { getProfileImage } from "@/utils/getProfileImage";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "PatientDoctorsDialog",
  mixins: [tourDialogMixin],
  props: {
    item: { type: Object, required: true },
    value: { type: Boolean, default: false },
  },
  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },
  data() {
    return {
      currentStep: 1,
      doctors: [],
      doctorsRelated: [],
      selectedDoctor: null,
      selectedClinic: null,
      clinics: [],
      userType: null,
      doctorToDelete: null,
      formValid: false,
      loadingDoctors: false,
      creatingRelation: false,
      deletingRelation: false,
      loadingRelatedDoctors: false,
      _doctorBlobUrls: new Set(),
      hasFetchedDoctors: false,
    };
  },
  computed: {
    tourId() {
      return "dialog-medicos-paciente";
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.tryOpen();
          this.tourOnOpen(); // tour guiado na 1ª vez que abre
        } else this.resetDialog();
      },
    },
    "item.id": {
      immediate: true,
      handler() {
        if (this.value) this.tryOpen();
      },
    },
  },
  beforeDestroy() {
    this.revokeDoctorBlobs();
  },
  methods: {
    tourSteps() {
      return [
        {
          title: "Médicos do paciente",
          description:
            "Aqui você vê e gerencia quais médicos estão vinculados a este paciente — útil para assistentes que organizam os atendimentos da clínica.",
        },
        {
          anchor: "pd-lista",
          title: "Lista de médicos",
          description:
            "Nesta etapa aparecem os médicos já vinculados ao paciente. Você pode consultar e, se precisar, remover vínculos.",
        },
        {
          anchor: "pd-add",
          title: "Adicionar relação",
          description:
            "Para vincular um novo médico, vá para esta etapa: escolha a clínica e o médico e confirme. O paciente passa a aparecer na agenda desse profissional.",
        },
      ];
    },

    // ================= LIFECYCLE =================
    async onOpen() {
      await this.init();
      await this.fetchData();
    },
    async tryOpen() {
      if (!this.item?.id) return;

      await this.$nextTick();
      await this.onOpen();
    },
    async init() {
      try {
        await fetchMe(); // garante sync com backend (se seu fluxo precisar)
        this.userType = this.$store.getters["auth/userType"];
      } catch (err) {
        console.error("Erro ao obter tipo de usuário:", err);
        this.userType = null;
      }
    },
    async fetchData() {
      await this.getMyDoctorsByMyPatientId();
    },
    closeDialog() {
      this.$emit("input", false);
    },
    resetDialog() {
      this.currentStep = 1;
      this.doctors = [];
      this.doctorsRelated = [];
      this.selectedDoctor = null;
      this.selectedClinic = null;
      this.clinics = [];
      this.userType = null;
      this.formValid = false;
      this.revokeDoctorBlobs();
      this.$refs.form?.resetValidation?.();
    },

    // ================= BLOB CLEANUP =================
    revokeDoctorBlobs() {
      if (!this._doctorBlobUrls) {
        this._doctorBlobUrls = new Set();
        return;
      }
      for (const url of this._doctorBlobUrls) {
        try {
          if (url && String(url).startsWith("blob:")) URL.revokeObjectURL(url);
        } catch {}
      }
      this._doctorBlobUrls.clear();
    },
    // ================= STEPS =================
    goToStep1() {
      this.currentStep = 1;
      this.selectedDoctor = null;
      this.selectedClinic = null;
    },
    async goToStep2() {
      if (this.userType !== "assistant") return;
      this.currentStep = 2;
      await this.setupRelationForm();
    },
    async setupRelationForm() {
      if (
        this.userType === "doctor_solo" ||
        this.userType === "doctor_clinic"
      ) {
        const myId = this.$store.getters["auth/userId"];
        this.selectedDoctor = { id: myId };
        return;
      }
      if (this.userType === "assistant") {
        this.clinics = (await getMyClinics()) || [];
        if (this.clinics.length > 0) {
          this.selectedClinic = this.clinics[0].id;
          await this.onClinicChange();
        }
      }
    },
    async onClinicChange() {
      if (!this.selectedClinic) {
        this.doctors = [];
        return;
      }
      if (this.userType !== "assistant") return;
      this.loadingDoctors = true;
      try {
        this.doctors = (await getDoctorsByClinics(this.selectedClinic)) || [];
      } catch (err) {
        console.error("Erro ao carregar médicos:", err);
        this.doctors = [];
      } finally {
        this.loadingDoctors = false;
      }
    },
    // ================= LISTAGEM =================
    async getMyDoctorsByMyPatientId() {
      if (!this.item?.id) return;
      this.loadingRelatedDoctors = true;
      this.hasFetchedDoctors = false;
      this.revokeDoctorBlobs();
      try {
        const { data } = await http.get(
          `/api/relation/doctors-by-patient/${this.item.id}`,
        );
        const doctorsRaw = data?.data || [];
        this.doctorsRelated = await Promise.all(
          doctorsRaw.map(async (doctor) => {
            // se backend já mandar URL pronta, usa ela
            if (doctor.profile_image_url) {
              return { ...doctor, imageUrl: doctor.profile_image_url };
            }
            // senão usa util (fallback + http padronizado)
            const imageUrl = await getProfileImage(doctor.profile_image);
            if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
              if (!this._doctorBlobUrls) this._doctorBlobUrls = new Set();
              this._doctorBlobUrls.add(imageUrl);
            }
            return { ...doctor, imageUrl };
          }),
        );
      } catch (err) {
        console.error("Erro ao buscar médicos:", err);
        this.doctorsRelated = [];
      } finally {
        this.loadingRelatedDoctors = false;
        this.hasFetchedDoctors = true;
      }
    },
    // ================= RELAÇÃO =================
    async createRelation() {
      if (!this.selectedDoctor || !this.item?.id) return;
      this.creatingRelation = true;
      const relation = {
        patient_id: this.item.id,
        doctor_id: this.selectedDoctor.id ?? this.selectedDoctor,
        relation_type: "doctor_patient",
      };
      try {
        await http.post(`/api/relation/doctor-patient`, relation);
        this.success("Relação criada com sucesso!");
        await this.getMyDoctorsByMyPatientId();
        this.goToStep1();
      } catch (err) {
        if (err.response?.status === 409) {
          this.warning("Esta relação já existe.");
        } else {
          console.error("Erro ao criar relação:", err);
          this.erroDeRequisicao(err, "Erro ao criar relação.");
        }
      } finally {
        this.creatingRelation = false;
      }
    },
    // ================= EXCLUSÃO =================
    async confirmDeleteDoctor(doctorId) {
      this.doctorToDelete = doctorId;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Tem certeza que deseja remover esta relação médico-paciente?",
        hint: "Esta ação não pode ser desfeita.",
      });
      if (!ok) {
        this.doctorToDelete = null;
        return;
      }
      await this.handleDeleteConfirmed();
    },
    async handleDeleteConfirmed() {
      if (!this.doctorToDelete || !this.item?.id) return;

      this.deletingRelation = true;

      const doctorId = this.doctorToDelete;
      const patientId = this.item.id;

      try {
        await http.delete(
          `/api/relation/doctor-patient/${doctorId}/${patientId}`,
        );

        this.success("Relação excluída com sucesso!");
        await this.getMyDoctorsByMyPatientId();
      } catch (err) {
        if (err.response?.status === 404) {
          this.erroDeRequisicao(err, "Relação não encontrada.");
        } else {
          console.error("Erro ao excluir relação:", err);
          this.erroDeRequisicao(err, "Erro ao excluir relação.");
        }
      } finally {
        this.doctorToDelete = null;
        this.deletingRelation = false;
      }
    },
  },
};
</script>

<style scoped>
.add-doctor-btn {
  font-size: 16px !important;
  padding: 12px 24px !important;
  border-radius: 8px !important;
}

.doctor-card {
  transition: all 0.3s ease;
  border-radius: 8px !important;
}

.doctor-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Cabeçalho do stepper: cartão branco sobre o corpo areia */
.steps-header {
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: var(--ds-radius);
  box-shadow: none !important;
}

.clinic-select,
.doctor-select {
  margin-bottom: 8px;
}

/* Melhorias nos cards de médicos */
.v-card-title {
  padding-bottom: 8px !important;
}

/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.ds-dialog-header--danger {
  background: linear-gradient(135deg, var(--ds-danger) 0%, var(--ds-danger-mid) 60%, var(--ds-danger-light) 100%) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
</style>
